import {
  FilterList as FilterListIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getVariables } from "../../../api/variables";
import { useErrorHandler } from "../../../hooks";
import { UploadMeasurementLine } from "../../../types/interfaces/file/imports/flowTestMeasurements";
import { VariableResponse } from "../../../types/model/variable";
import { formatTimestamp } from "../../../utils";
import { UploadMeasurementForm } from "./UploadMeasurementForm";

interface RowProps {
  line: UploadMeasurementLine;
  open: boolean;
  onToggle: () => void;
  variables: VariableResponse[];
}

const Row = ({ line, open, onToggle, variables }: RowProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const preview = line.preview;
  if (!preview) return null;

  const getTimeDisplay = () => {
    if (preview.testingTime != null) return `${preview.testingTime} ${t("generic.units.minutes")}`;
    if (preview.measuredAt) return formatTimestamp(preview.measuredAt);
    return "";
  };

  const timeDisplay = getTimeDisplay();
  const variableName = variables.find(v => v.id === preview.variableId)?.name ?? String(preview.variableId);
  const variableUnit = variables.find(v => v.id === preview.variableId)?.unit ?? "";

  return (
    <React.Fragment>
      <TableRow
        onClick={onToggle}
        sx={{ cursor: "pointer", "& > *": { borderBottom: "unset" } }}
      >
        <TableCell>
          {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </TableCell>
        <TableCell>{timeDisplay}</TableCell>
        <TableCell component="th" scope="row">
          {preview.measuredCaptureIdentifier}
        </TableCell>
        <TableCell>{variableName}</TableCell>
        <TableCell>{preview.flag} {preview.value} {variableUnit}</TableCell>
      </TableRow>
      <TableRow sx={open ? { backgroundColor: theme.palette.action.hover } : {}}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <UploadMeasurementForm measurement={preview} variableName={variableName} variableUnit={variableUnit} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

interface UploadMeasurementsPreviewTableProps {
  lines: UploadMeasurementLine[];
}

export function UploadMeasurementsPreviewTable({
  lines,
}: Readonly<UploadMeasurementsPreviewTableProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [filter, setFilter] = useState("");
  const [openRowId, setOpenRowId] = useState<number | null>(null);
  const [variables, setVariables] = useState<VariableResponse[]>([]);

  useEffect(() => {
    const fetchVariables = async () => {
      try {
        const data = await getVariables(false);
        setVariables(data);
      } catch (error) {
        handleError(error);
      }
    };
    fetchVariables();
  }, []);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
    setPage(0);
  };

  const filteredData = lines.filter((line) => {
    const preview = line.preview;
    if (!preview) return false;

    const variableName =
      variables.find((v) => v.id === preview.variableId)?.name ?? "";

    const variableUnit =
      variables.find((v) => v.id === preview.variableId)?.unit ?? "";

    const search = filter.trim().toLowerCase();

    if (!search) return true;

    return [
      preview.measuredCaptureIdentifier,
      variableName,
      variableUnit,
      preview.measuredAt,
      preview.testingTime,
      preview.value,
      preview.flag,
      preview.notes,
    ]
      .filter(Boolean)
      .some((value) =>
        String(value).toLowerCase().includes(search)
      );
  });

  const visibleRows = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100%" }} variant="h5">
          {t("imports.confirm-dialog.data-table-title")}
        </Typography>
        <Tooltip title={t("generic.filter")}>
          <IconButton onClick={() => setShowFilters(!showFilters)}>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>

      {showFilters && (
        <Grid2 container spacing={3} sx={{ p: 2 }}>
          <Grid2 size={12}>
            <TextField
              label={t("generic.search")}
              value={filter}
              onChange={handleFilterChange}
              fullWidth
            />
          </Grid2>
        </Grid2>
      )}

      <TableContainer>
        <Table aria-label="collapsible table" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "5%" }} />
              <TableCell>{t("flow-tests.flow-test-measurement.measured-at-or-testing-time")}</TableCell>
              <TableCell>{t("flow-tests.flow-test-measurement.measured-capture")}</TableCell>
              <TableCell>{t("flow-tests.flow-test-measurement.variable")}</TableCell>
              <TableCell>{t("flow-tests.flow-test-measurement.value")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((line) => (
              <Row
                key={line.line}
                line={line}
                open={openRowId === line.line}
                onToggle={() =>
                  setOpenRowId(openRowId === line.line ? null : line.line)
                }
                variables={variables}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number.parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Paper>
  );
}